import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MoreVertical, ShieldOff, UserX, VolumeX } from 'lucide-react';
import clsx from 'clsx';
import { channelsApi } from '@/api/channels';
import { useAuthStore } from '@/store/authStore';
import Avatar from '@/components/ui/Avatar';
import { ChannelMember, ChannelRole } from '@/types';
import { useKarmaStore } from '@/store/karmaStore';

const ROLE_ORDER: Record<ChannelRole, number> = {
  OWNER: 0, ADMIN: 1, MODERATOR: 2, MEMBER: 3, GUEST: 4,
};

const ROLE_COLORS: Record<ChannelRole, string> = {
  OWNER: 'text-yellow-400',
  ADMIN: 'text-red-400',
  MODERATOR: 'text-blue-400',
  MEMBER: 'text-surface-400',
  GUEST: 'text-surface-600',
};

interface Props { channelId: string; }

type MemberUserWithNick = ChannelMember['user'] & {
  nickColor?: string | null;
  nickIcon?: string | null;
};

export default function MembersPanel({ channelId }: Props) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const karmaGet = useKarmaStore((s) => s.get);
  const [actionMenu, setActionMenu] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['channel-members', channelId],
    queryFn: () => channelsApi.getMembers(channelId, { limit: 100 }),
  });

  const members = [...(data?.data ?? [])].sort(
    (a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role],
  );

  const canModerate = (member: ChannelMember): boolean => {
    const myMember = members.find((m) => m.userId === user?.id);
    if (!myMember) return false;
    return ROLE_ORDER[myMember.role] < ROLE_ORDER[member.role];
  };

  const handleKick = async (member: ChannelMember) => {
    await channelsApi.kick(channelId, member.userId);
    setActionMenu(null);
  };

  const handleBan = async (member: ChannelMember) => {
    const reason = prompt('Причина (необязательно):') ?? undefined;
    await channelsApi.ban(channelId, member.userId, reason);
    setActionMenu(null);
  };

  const handleMute = async (member: ChannelMember) => {
    await channelsApi.mute(channelId, member.userId, 30 * 60 * 1000, 'Muted by moderator');
    setActionMenu(null);
  };

  const roleGroups = ['OWNER', 'ADMIN', 'MODERATOR', 'MEMBER', 'GUEST'] as ChannelRole[];

  return (
    <aside className="w-56 border-l border-surface-800 bg-surface-900 flex flex-col flex-shrink-0">
      <div className="h-12 px-4 flex items-center justify-between border-b border-surface-800">
        <span className="text-surface-300 text-sm font-semibold">Участники — {members.length}</span>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {roleGroups.map((role) => {
          const group = members.filter((m) => m.role === role);
          if (!group.length) return null;
          return (
            <div key={role} className="mb-3">
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-surface-500 mb-1">
                {role} — {group.length}
              </p>
              {group.map((member) => (
                <div
                  key={member.id}
                  className="relative flex items-center gap-2 px-3 py-1.5 hover:bg-surface-800 group cursor-pointer"
                  onClick={() => navigate(`/u/${member.user.username}`)}
                >
                  <Avatar
                    src={member.user.avatarUrl}
                    username={member.user.username}
                    size="xs"
                    status={member.user.status as never}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={clsx('text-xs font-medium truncate flex items-center gap-1', ROLE_COLORS[member.role])}
                      style={(member.user as MemberUserWithNick).nickColor ? { color: (member.user as MemberUserWithNick).nickColor as string } : undefined}
                      title={(() => {
                        const k = karmaGet(member.userId);
                        return k !== null ? `★ ${k.toFixed(1)}` : undefined;
                      })()}
                    >
                      {(member.user as MemberUserWithNick).nickIcon && (
                        <img src={(member.user as MemberUserWithNick).nickIcon as string} alt="" className="w-[13px] h-[13px] flex-shrink-0" />
                      )}
                      {member.user.displayName}
                    </p>
                  </div>

                  {canModerate(member) && (
                    <button
                      onClick={() => setActionMenu(actionMenu === member.id ? null : member.id)}
                      className="opacity-0 group-hover:opacity-100 btn-ghost p-0.5"
                    >
                      <MoreVertical size={13} />
                    </button>
                  )}

                  {actionMenu === member.id && (
                    <div className="absolute right-3 top-8 z-10 bg-surface-800 border border-surface-700 rounded-lg shadow-xl text-xs w-36">
                      <button
                        onClick={() => handleKick(member)}
                        className="flex items-center gap-2 w-full px-3 py-2 hover:bg-surface-700 text-surface-200"
                      >
                        <UserX size={12} /> Выгнать
                      </button>
                      <button
                        onClick={() => handleBan(member)}
                        className="flex items-center gap-2 w-full px-3 py-2 hover:bg-surface-700 text-red-400"
                      >
                        <ShieldOff size={12} /> Забанить
                      </button>
                      <button
                        onClick={() => handleMute(member)}
                        className="flex items-center gap-2 w-full px-3 py-2 hover:bg-surface-700 text-surface-200"
                      >
                        <VolumeX size={12} /> Мут 30м
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
